<template>
  <v-container fluid class="import-page">
    <section class="imp-hero">
      <div>
        <div class="imp-kicker">
          <span class="imp-kicker__icon"
            ><v-icon small>fal fa-file-import</v-icon></span
          >
          {{ $t("data_management") }}
        </div>
        <h1>{{ $t("import_data") }}</h1>
        <p>{{ $t("import_page_subtitle") }}</p>
      </div>
      <div class="imp-hero__badge">
        <strong>{{ items.length + stockItems.length }}</strong>
        <span>{{ $t("import_sources") }}</span>
      </div>
    </section>

    <ol class="imp-steps">
      <li v-for="(step, i) in importSteps" :key="step.key" class="imp-step">
        <span class="imp-step__num">{{ i + 1 }}</span>
        <span class="imp-step__icon"><v-icon small>{{ step.icon }}</v-icon></span>
        <span class="imp-step__text">{{ $t(step.key) }}</span>
      </li>
    </ol>

    <section v-for="group in groups" :key="group.key" class="imp-section">
      <div class="imp-section__heading">
        <div>
          <h2>{{ $t(group.title) }}</h2>
          <p v-if="group.key === 'stock_import'" class="imp-notice">
            <v-icon x-small>fal fa-shield-check</v-icon>
            {{ $t("stock_import_notice") }}
          </p>
          <p v-else>{{ $t("choose_import_type") }}</p>
        </div>
        <span class="imp-section__count">{{ group.items.length }}</span>
      </div>

      <div class="imp-grid">
        <article
          v-for="(item, index) in group.items"
          :key="item.api"
          class="imp-card"
          :style="{ '--imp-accent': accent(index, group.key) }"
        >
          <div class="imp-card__head">
            <span class="imp-card__icon"><v-icon>{{ item.icon }}</v-icon></span>
            <span class="imp-card__index">{{ String(index + 1).padStart(2, "0") }}</span>
          </div>
          <div class="imp-card__body">
            <h3>{{ $t(item.title) }}</h3>
            <p>{{ $t("manage_imports", { name: $t(item.title) }) }}</p>
            <span class="imp-card__format">
              <v-icon x-small>fal fa-file-excel</v-icon>
              CSV · XLSX
            </span>
          </div>
          <div class="imp-card__actions">
            <v-btn
              text
              small
              class="imp-card__template text-none"
              :loading="downloading === item.api"
              @click="exampleAction(item)"
            >
              <v-icon left x-small>fal fa-download</v-icon>
              {{ $t("example") }}
            </v-btn>
            <v-btn
              depressed
              small
              color="primary"
              class="imp-card__import text-none"
              :disabled="previewing || committing"
              @click="importAction(item)"
            >
              <v-icon left x-small>fal fa-file-import</v-icon>
              {{ $t("import") }}
            </v-btn>
          </div>
        </article>
      </div>
    </section>

    <app-custom-dialog
      v-model="dialog"
      :footer="true"
      :loading="importing"
      title="import_data"
      class="import-dialog"
      @submit="submitForm"
    >
      <div slot="activator">
        <v-col cols="12">
          <div class="section-label">{{ $t("file_upload") }}</div>
          <v-file-input
            ref="importFileInput"
            :key="fileInputKey"
            v-model="file"
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            outlined
            counter
            show-size
            color="#438ef2"
            prepend-icon=""
            prepend-inner-icon="fal fa-paperclip"
            class="import-file-input"
            @click:prepend-inner="openFileDialog"
          >
          </v-file-input>
        </v-col>
      </div>
    </app-custom-dialog>

    <ingredient-unit-conversion-compare-dialog
      v-model="ingredientUnitConversionCompareDialog"
      :compare-data="ingredientUnitConversionCompareData"
      :confirming="confirmingIngredientUnitConversionImport"
      @confirm="onConfirmIngredientUnitConversionImport"
    />

    <product-recipe-compare-dialog
      v-model="productRecipeCompareDialog"
      :compare-data="productRecipeCompareData"
      :confirming="confirmingProductRecipeImport"
      @confirm="onConfirmProductRecipeImport"
    />

    <!-- Stock imports (Product Stock / Ingredient Stock) go through an extra
         preview/compare step before anything is written, so a wrong file
         can't silently overwrite stock levels. Same dialog chrome as
         ProductRecipeCompareDialog/IngredientUnitConversionCompareDialog
         (app-custom-dialog: plain white title bar, red circular close
         button, outline-cancel + solid-confirm footer) instead of this
         dialog's old bespoke blue banner header. -->
    <app-custom-dialog
      :value="compareDialog"
      title="stock_compare_title"
      width="90vw"
      dialog-height="85vh"
      :flex-layout="true"
      :footer="true"
      :close-btn="true"
      cancel-text="cancel"
      submit-text="confirm"
      :disable-submit="!canConfirmStockImport"
      :loading="committing"
      @input="(val) => (val ? (compareDialog = val) : cancelStockCompare())"
      @submit="confirmStockImport"
    >
      <div slot="activator" class="import-compare-activator">
        <template v-if="comparePreview">
          <div v-if="previewRef" class="import-compare-meta">
            <span>{{ $t("code") }} {{ previewRef }}</span>
            <span class="import-compare-meta-sep">&middot;</span>
            <span>{{ todayLabel }}</span>
          </div>

          <!-- Rows the server is going to reject are caught HERE, before the
               upload, instead of coming back as a wall of
               `identifier_is_required` in the result dialog after the fact. -->
          <div v-if="summaryFailed > 0" class="import-compare-error-banner">
            <v-icon size="14" color="#b3261e">fal fa-exclamation-triangle</v-icon>
            <span>
              <template v-if="allRowsInvalid">
                {{ $t("preview_all_rows_invalid") }}
              </template>
              <template v-else>
                {{ $t("preview_some_rows_invalid", { count: summaryFailed }) }}
              </template>
            </span>
          </div>

          <ul class="import-compare-stats">
            <li class="import-compare-stat import-compare-stat--total">
              <span class="import-compare-stat-value">{{ summaryTotal }}</span>
              <span class="import-compare-stat-label">{{ $t("total_rows") }}</span>
            </li>
            <li class="import-compare-stat import-compare-stat--updated">
              <span class="import-compare-stat-value">{{ summaryWillUpdate }}</span>
              <span class="import-compare-stat-label">{{ $t("will_update") }}</span>
            </li>
            <li class="import-compare-stat import-compare-stat--unchanged">
              <span class="import-compare-stat-value">{{ summaryUnchanged }}</span>
              <span class="import-compare-stat-label">{{ $t("unchanged") }}</span>
            </li>
            <li
              v-if="summaryFailed > 0"
              class="import-compare-stat import-compare-stat--failed"
            >
              <span class="import-compare-stat-value">{{ summaryFailed }}</span>
              <span class="import-compare-stat-label">{{ $t("error") }}</span>
            </li>
          </ul>

          <div class="import-compare-table-wrap">
            <table class="import-compare-table">
              <thead>
                <tr>
                  <th scope="col">{{ $t("row") }}</th>
                  <th scope="col">{{ $t("code") }}</th>
                  <th
                    v-if="showProductColumn"
                    scope="col"
                    class="import-compare-name-cell"
                  >
                    {{ $t("product") }}
                  </th>
                  <th scope="col" class="import-compare-name-cell">{{ $t("name") }}</th>
                  <th scope="col" class="import-compare-qty-cell">{{ $t("stock_db_now") }}</th>
                  <th scope="col" class="import-compare-qty-cell">{{ $t("stock_excel_add") }}</th>
                  <th scope="col" class="import-compare-qty-cell">{{ $t("stock_final") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, idx) in comparePreview.rows"
                  :key="idx"
                  :class="{
                    'import-compare-row--stripe': idx % 2 === 1,
                    'import-compare-row--error': isFailedRow(row),
                  }"
                >
                  <td>{{ row.row || "-" }}</td>
                  <!-- resolveRowName()/resolveRowCode() try every field
                       shape a backend response has used (row.name,
                       row.product_name, row.product?.name, etc.) before
                       falling back to parsing a raw "<code> - <name>" label
                       — see their docblocks. A dash only appears once none
                       of those actually has a value. -->
                  <td>{{ resolveRowCode(row) || "-" }}</td>
                  <td v-if="showProductColumn" class="import-compare-name-cell">
                    {{ row.product || "-" }}
                  </td>
                  <td class="import-compare-name-cell">
                    {{ resolveRowName(row) || "-" }}
                  </td>
                  <td class="import-compare-qty-cell">{{ qtyOrDash(row.current) }}</td>
                  <td class="import-compare-qty-cell">{{ qtyOrDash(row.new) }}</td>
                  <td class="import-compare-qty-cell">
                    <span
                      v-if="isFailedRow(row)"
                      class="import-compare-status import-compare-status--error"
                    >
                      {{ $t(row.error) }}
                    </span>
                    <!-- The real backend preview (Ingredient Stock) sends the
                         actual result = current + new directly — show that
                         number as-is. Only the client-side demo compare
                         (Product Stock, which has no backend preview) has no
                         `result` field, so it keeps falling back to the old
                         new-minus-current diff label below. -->
                    <span v-else-if="row.result !== undefined && row.result !== null">{{
                      row.result
                    }}</span>
                    <span v-else :class="diffClass(row)">{{
                      diffLabel(row)
                    }}</span>
                  </td>
                </tr>
                <tr v-if="!comparePreview.rows.length">
                  <td :colspan="showProductColumn ? 7 : 6" class="import-compare-empty">
                    {{ $t("no_data") }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
    </app-custom-dialog>
  </v-container>
</template>

<script>
import { success, toast } from "@/controllers/alert";
import IngredientUnitConversionCompareDialog from "@/components/settings/IngredientUnitConversionCompareDialog";
import ProductRecipeCompareDialog from "@/components/settings/ProductRecipeCompareDialog";

const STOCK_IMPORT_APIS = new Set(["product_stock", "ingredient_stock", "variant_stock"]);

/**
 * Excel-parsing error codes that mean "this row could not be matched to an
 * existing product/ingredient" — NOT a bad/invalid row. The current backend
 * (ExcelController::buildStockPreview()) no longer sends these at all: an
 * unmatched row now comes back with status "new" directly, so it is already
 * a normal preview row (see isFailedRow()). These codes are kept ONLY as a
 * defensive fallback — e.g. while running against a backend that hasn't
 * picked up the ExcelController change yet — so an older-shaped response
 * still renders as a new-item row instead of a red error row. Matched
 * case-insensitively against `row.error`.
 */
const NOT_FOUND_ERROR_CODES = new Set(["ingredient_not_found", "product_not_found"]);

/**
 * Spreadsheet parser, loaded on demand the first time an .xlsx is previewed
 * so it never enters the main bundle.
 *
 * If the project can take the dependency (`npm i xlsx`), delete this and
 * `loadXlsx()` and put `import * as XLSX from "xlsx";` at the top instead —
 * a pinned dependency beats a runtime CDN fetch, and it keeps working behind
 * a strict CSP or with no outbound network.
 */
const XLSX_CDN =
  "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";

/**
 * Header aliases for the client-side preview parse.
 *
 * The current stock template ships just `stock | name` (the identifier IS the
 * column the user fills in). The older template shipped
 * `stock | select_product | [code] | name | id`, so those headers stay listed
 * too — an old file someone still has on disk must keep previewing correctly.
 */
const COLUMN_ALIASES = {
  code: [
    "code",
    "sku",
    "barcode",
    "product_code",
    "ingredient_code",
    "รหัส",
    "รหัสสินค้า",
    "ລະຫັດ",
    "ລະຫັດສິນຄ້າ",
    "编码",
    "商品编码",
  ],
  name: [
    "name",
    "product",
    "select_product",
    "ingredient",
    "select_ingredient",
    "ingredient_name",
    "product_name",
    "title",
    "ชื่อ",
    "ชื่อสินค้า",
    "ชื่อวัตถุดิบ",
    "ຊື່",
    "ຊື່ສິນຄ້າ",
    "名称",
    "商品名称",
  ],
  qty: [
    "stock",
    "stock_qty",
    "current_stock",
    "qty",
    "quantity",
    "amount",
    "จำนวน",
    "สต็อก",
    "ຈຳນວນ",
    "ສະຕັອກ",
    "数量",
    "库存",
  ],
  id: ["id", "product_id", "ingredient_id"],
};

/**
 * The dropdown/picker column headers on the `main` sheet — `select_ingredient`
 * (ingredient stock) or `select_product` (product stock). This is the ONE
 * column a customer actually fills in for every row past the two shipped
 * example rows; see the note in compareFromTable() for why it needs its own
 * alias list separate from COLUMN_ALIASES.name.
 */
const PICKER_COLUMN_ALIASES = ["select_ingredient", "select_product"];

/** Sheet the stock templates put the fill-in form on. */
const TEMPLATE_SHEET = "main";

/**
 * The visible reference tab the stock templates ship next to `main`
 * (`code | name | ingredient | id`). It is the fallback source for a `code`
 * the main sheet doesn't carry — either because the template dropped that
 * column entirely (it does when no item in the branch has a code), or because
 * the customer deleted the hidden columns before uploading.
 */
const LOOKUP_SHEETS = ["ingredient", "product"];

/**
 * Headers on that lookup sheet.
 *
 * `label` is column C — the exact text the dropdown offers and the customer
 * picks, which is what column B of `main` holds. Kept separate from `name` on
 * purpose: on the older ingredient template the label is "<code> - <name>",
 * not the bare name, so matching on `name` alone would miss.
 */
const LOOKUP_ALIASES = {
  code: COLUMN_ALIASES.code,
  name: [
    "name",
    "product_name",
    "ingredient_name",
    "title",
    "ชื่อ",
    "ชื่อสินค้า",
    "ชื่อวัตถุดิบ",
    "ຊື່",
    "ຊື່ສິນຄ້າ",
    "名称",
    "商品名称",
  ],
  label: [
    "ingredient",
    "product",
    "select_ingredient",
    "select_product",
    "label",
  ],
  id: COLUMN_ALIASES.id,
};

/**
 * NOTE: there is intentionally no cap on the number of rows shown here.
 * Every row in the client-parsed table (compareFromTable() below) is kept —
 * this function is only reached as a last-resort offline fallback (see
 * buildDemoCompare()), which is no longer triggered automatically now that
 * both Product Stock and Ingredient Stock have a real backend preview
 * endpoint; the real preview (ExcelController::buildStockPreview()) already
 * returns every row from the uploaded file with no server-side limit either.
 */

export default {
  components: {
    IngredientUnitConversionCompareDialog,
    ProductRecipeCompareDialog,
  },
  data: () => ({
    dialog: false,
    file: null,
    importing: false,
    ingredientUnitConversionCompareDialog: false,
    ingredientUnitConversionCompareData: null,
    productRecipeCompareDialog: false,
    productRecipeCompareData: null,
    // Same reason as pendingIngredientUnitConversionFile: kept around after
    // a successful compare so confirm() can resend the exact same file.
    pendingProductRecipeFile: null,
    confirmingProductRecipeImport: false,
    // The uploaded File object is kept around after a successful "compare"
    // so confirm() can resend the exact same file to the real import
    // endpoint — the user shouldn't have to pick the file again.
    pendingIngredientUnitConversionFile: null,
    confirmingIngredientUnitConversionImport: false,
    fileInputKey: 0,
    downloading: null,
    select: {
      title: "product",
      value: 1,
      icon: "fal fa-shopping-cart",
      api: "products",
    },
    // Stock imports get an extra preview/compare step before commit
    compareDialog: false,
    comparePreview: null, // { token, summary, rows }
    previewing: false,
    committing: false,
    // Master-data imports (categories, units, products, etc.)
    items: [
      {
        title: "category",
        value: 4,
        icon: "fal fa-badge-check",
        api: "categories",
      },
      { title: "unit", value: 5, icon: "fal fa-box", api: "product_size" },
      {
        title: "product",
        value: 1,
        icon: "fal fa-shopping-cart",
        api: "products",
      },
      { title: "customer", value: 2, icon: "fal fa-users", api: "customers" },
      { title: "user", value: 3, icon: "fal fa-user-cog", api: "users" },
      {
        title: "product_options",
        value: 6,
        icon: "fal fa-sliders-h",
        api: "store_options",
      },
      {
        title: "ingredient",
        value: 7,
        icon: "fal fa-dolly",
        api: "ingredients",
      },
      {
        title: "ingredient_unit_conversion",
        value: 8,
        icon: "fal fa-exchange-alt",
        api: "ingredient_unit_conversion",
      },
      {
        title: "product_recipe",
        value: 9,
        icon: "fal fa-mortar-pestle",
        api: "product_recipe",
      },
    ],
    // Stock-level imports are kept in their own section.
    stockItems: [
      {
        title: "product_stock",
        value: 8,
        icon: "fal fa-boxes",
        api: "product_stock",
      },
      {
        title: "ingredient_stock",
        value: 9,
        icon: "fal fa-warehouse",
        api: "ingredient_stock",
      },
      {
        title: "variant_stock",
        value: 10,
        icon: "fal fa-layer-group",
        api: "variant_stock",
      },
    ],
  }),
  created() {
    // Not in data(): the loader promise must not be reactive, and nothing in
    // the template reads it.
    this.xlsxPromise = null;
  },
  computed: {
    importSteps() {
      return [
        { key: "import_step_download", icon: "fal fa-download" },
        { key: "import_step_fill", icon: "fal fa-edit" },
        { key: "import_step_upload", icon: "fal fa-file-upload" },
      ];
    },
    groups() {
      return [
        { key: "import_data", title: "import_data", items: this.items },
        { key: "stock_import", title: "stock_import", items: this.stockItems },
      ];
    },
    isStockImportSelected() {
      return STOCK_IMPORT_APIS.has(this.select.api);
    },
    previewRows() {
      return (
        (this.comparePreview && Array.isArray(this.comparePreview.rows)
          ? this.comparePreview.rows
          : []) || []
      );
    },
    // Variant Stock rows carry a `product` field (the variant's PARENT
    // product, e.g. "202606772 - Black Coffee") alongside `name` (the
    // variant's own label, e.g. "Less Sugar") — see
    // ExcelController::buildVariantStockPreview(). Product Stock/Ingredient
    // Stock rows have no such field, so the extra column only renders when
    // it would actually carry something.
    showProductColumn() {
      return this.previewRows.some((row) => row && row.product);
    },
    validPreviewRows() {
      // Demo compare (Product Stock) flags a bad row with status "error";
      // the real backend preview (Ingredient Stock) uses "failed" (see
      // ExcelController::IngredientStockPreview()). Both must be excluded,
      // otherwise a failed Ingredient Stock row was wrongly counted as
      // importable and Confirm stayed enabled even when every row failed.
      return this.previewRows.filter((row) => !this.isFailedRow(row));
    },
    allRowsInvalid() {
      return Number(this.summaryTotal) > 0 &&
        Number(this.summaryFailed) >= Number(this.summaryTotal);
    },
    // Confirm is only offered when at least one row can actually be imported.
    // The old version short-circuited on `isDemo`, so a file with nothing but
    // unidentifiable rows still uploaded and came back all-Failed.
    canConfirmStockImport() {
      return (
        !this.committing &&
        !!this.comparePreview &&
        (this.comparePreview.isDemo
          ? Number(this.previewSummary.will_update) > 0
          : this.validPreviewRows.length > 0)
      );
    },
    // Pulled out of the template so the markup doesn't depend on `??`
    // support in the template compiler, and so a preview payload with a
    // missing `summary` can't blow up the whole dialog.
    previewSummary() {
      return (this.comparePreview && this.comparePreview.summary) || {};
    },
    summaryTotal() {
      const total = this.previewSummary.total_rows;
      return total === undefined || total === null
        ? this.previewRows.length
        : total;
    },
    summaryWillUpdate() {
      return this.previewSummary.will_update || 0;
    },
    summaryUnchanged() {
      return this.previewSummary.unchanged || 0;
    },
    summaryFailed() {
      return this.previewSummary.failed_count || 0;
    },
    previewRef() {
      const token = this.comparePreview && this.comparePreview.token;
      return token ? String(token).slice(-4).toUpperCase() : "";
    },
    todayLabel() {
      const d = new Date();
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      return this.$t("date_label", {
        date: `${dd}/${mm}/${d.getFullYear()}`,
      });
    },
  },
  methods: {
    // Accent colour per card. Stock cards continue the palette so the two
    // sections don't repeat the same first colours.
    accent(index, groupKey) {
      const palette = ["#5b39c9", "#168b78", "#2774c7", "#c87528", "#475569"];
      return palette[(index + (groupKey === "stock_import" ? 3 : 0)) % palette.length];
    },
    // Only pass a confirm-button label to SweetAlert when we actually have a
    // translation — otherwise let it use its own localized default instead
    // of printing the raw key "ok".
    confirmLabel() {
      const label = this.$t("ok");
      return label && label !== "ok" ? label : undefined;
    },
    getColor(seed) {
      const colors = [
        "#00BCD4", // cyan
        "#009688", // teal
        "#2196F3", // blue
        "#3F51B5", // indigo
        "#673AB7", // deep-purple
        "#607D8B", // blue-grey
        "#4CAF50", // green
        "#FF9800", // orange
        "#FF5722", // deep-orange
        "#795548", // brown
      ];
      const n = Number(seed) || 0;
      return colors[n % colors.length];
    },
    async exampleAction(item) {
      if (this.downloading) return;
      this.downloading = item.api;
      let url = null;
      try {
        const res = await this.$http.get(`${item.api}_format?locale=${this.$i18n.locale}`, {
          responseType: "blob",
          // Don't let axios reject on 4xx/5xx. The app's interceptor catches
          // rejected requests and resolves with `undefined`, which threw the
          // server's error body away — that's why a 500 could only surface as
          // "Cannot read properties of undefined (reading 'data')". Accepting
          // every status keeps the response, so we can read what went wrong.
          validateStatus: () => true,
        });

        if (!res || !res.data) {
          throw new Error(
            this.$t("empty_response", { url: `${item.api}_format` })
          );
        }

        // An error response is a JSON/HTML body wearing a blob's clothes, not
        // an .xlsx. Read it out so the dialog shows Laravel's actual message.
        const type = (res.data.type || "").toLowerCase();
        const isErrorBody =
          res.status >= 400 ||
          type.includes("json") ||
          type.includes("html") ||
          type.includes("text/plain");

        if (isErrorBody) {
          const text = await res.data.text();
          let message = text;
          try {
            const body = JSON.parse(text);
            // Laravel debug responses carry message + exception + file + line
            message = body.message || text;
            if (body.exception) {
              message += `\n\n${body.exception}`;
            }
            if (body.file) {
              message += `\n${body.file}:${body.line || "?"}`;
            }
          } catch (e) {
            /* not JSON (an HTML error page) — show the raw body */
          }
          throw new Error(
            `HTTP ${res.status} — ${String(message).slice(0, 800)}`
          );
        }

        url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        const date = new Date();
        link.setAttribute(
          "download",
          `${this.$t(item.title)}-${date.getDate()}-${
            date.getMonth() + 1
          }-${date.getFullYear()}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        await toast(this, "downloaded_format_file_successfully");
      } catch (err) {
        await this.showRequestError(
          this.$t("download_failed"),
          this.describeError(err)
        );
      } finally {
        // the old code never released this, leaking the blob for the life of
        // the tab every time someone downloaded a template
        if (url) window.URL.revokeObjectURL(url);
        this.downloading = null;
      }
    },
    importAction(item) {
      if (this.previewing || this.committing) return;
      this.comparePreview = null;
      this.compareDialog = false;
      // clear any file left over from a previous (possibly cancelled) import
      // — otherwise picking "Products", cancelling, then opening "Customers"
      // would submit the products file against the customers endpoint
      this.resetImportFile();
      this.select = item;
      this.dialog = true;
    },
    // The paperclip icon is only decorative by default in Vuetify's
    // v-file-input — clicking it doesn't open the OS file picker. Forward
    // the click to the component's hidden native <input type="file">.
    openFileDialog() {
      const input =
        this.$refs.importFileInput &&
        this.$refs.importFileInput.$refs.input;
      if (input) input.click();
    },
    resetImportFile() {
      // Recreate Vuetify's native file input so choosing the same file again
      // always triggers a new change event.
      this.file = null;
      this.fileInputKey += 1;
    },
    async submitForm() {
      // Guards against the OK button (or Enter) firing a second upload
      // while a previous one (or a stock preview/commit) is still in flight.
      if (this.importing || this.previewing || this.committing) return;
      if (!this.file) return;

      // ingredient_unit_conversion and product_recipe never write directly
      // from this dialog — both first compare the edited excel against
      // current data so the user can review the diff, then confirm/cancel
      // from that popup instead.
      if (this.select.api === "ingredient_unit_conversion") {
        await this.compareIngredientUnitConversionImport();
        return;
      }
      if (this.select.api === "product_recipe") {
        await this.compareProductRecipeImport();
        return;
      }

      // Product Stock / Ingredient Stock: preview first, commit only after
      // the user reviews the compare table and confirms.
      if (this.isStockImportSelected) {
        await this.previewStockImport();
        return;
      }

      await this.submitDirectImport();
    },
    async compareIngredientUnitConversionImport() {
      this.importing = true;
      try {
        const formData = new FormData();
        formData.append("file", this.file);
        const { data } = await this.$http.post(
          "ingredient_unit_conversion_compare",
          formData,

          { validateStatus: () => true }
        );
        if (data.status) {
          this.pendingIngredientUnitConversionFile = this.file;
          this.file = null;
          this.dialog = false;
          this.ingredientUnitConversionCompareData = data.data;
          this.ingredientUnitConversionCompareDialog = true;
        } else {
          await this.showRequestError(
            this.$t("import_failed"),
            this.translateServerMessage(data.message) || this.$t("server_rejected_file")
          );
        }
      } catch (err) {
        await this.showRequestError(
          this.$t("import_failed"),
          this.describeError(err)
        );
      } finally {
        this.importing = false;
      }
    },
    async compareProductRecipeImport() {
      this.importing = true;
      try {
        const formData = new FormData();
        formData.append("file", this.file);
        const { data } = await this.$http.post(
          "product_recipe_compare",
          formData,
          { validateStatus: () => true }
        );
        if (data.status) {
          this.pendingProductRecipeFile = this.file;
          this.file = null;
          this.dialog = false;
          this.productRecipeCompareData = data.data;
          this.productRecipeCompareDialog = true;
        } else {
          await this.showRequestError(
            this.$t("import_failed"),
            this.translateServerMessage(data.message) || this.$t("server_rejected_file")
          );
        }
      } catch (err) {
        await this.showRequestError(
          this.$t("import_failed"),
          this.describeError(err)
        );
      } finally {
        this.importing = false;
      }
    },
    async submitDirectImport() {
      try {
        const formData = new FormData();
        formData.append("file", this.file);
        const { data } = await this.$http.post(
          `${this.select.api}_import`,
          formData,
          { validateStatus: () => true }
        );
        if (!data.status) {
          await this.showRequestError(
            this.$t("import_failed"),
            this.translateServerMessage(data.message) || this.$t("server_rejected_file")
          );
          return;
        }
        this.file = null;
        this.dialog = false;
        // some imports (e.g. product option) return a row-level summary
        const summary = data.data;
        if (summary && Array.isArray(summary.failed_rows)) {
          await this.showImportSummary(summary);
        } else {
          success(this);
        }
      } catch (err) {
        // the old code had no catch here, so a network/500 error just left
        // the dialog sitting there with no feedback at all
        await this.showRequestError(
          this.$t("import_failed"),
          this.describeError(err)
        );
      } finally {
        this.resetImportFile();
      }
    },
    async onConfirmProductRecipeImport() {
      // Guards against double-submit — the confirm button is disabled while
      // confirming, but this mirrors the same guard submitForm() uses.
      if (this.confirmingProductRecipeImport) return;
      if (!this.pendingProductRecipeFile) return;

      this.confirmingProductRecipeImport = true;
      try {
        const formData = new FormData();
        formData.append("file", this.pendingProductRecipeFile);
        const { data } = await this.$http.post(
          "product_recipe_import",
          formData,
          // See the matching comment on the ingredient-unit-conversion
          // compare call above.
          { validateStatus: () => true }
        );
        if (data.status) {
          this.pendingProductRecipeFile = null;
          this.productRecipeCompareDialog = false;
          await this.showProductRecipeImportResult(data.data);
        } else {
          await this.showRequestError(
            this.$t("import_failed"),
            this.translateServerMessage(data.message) || this.$t("server_rejected_file")
          );
        }
      } catch (err) {
        await this.showRequestError(
          this.$t("import_failed"),
          this.describeError(err)
        );
      } finally {
        this.confirmingProductRecipeImport = false;
      }
    },
    async onConfirmIngredientUnitConversionImport() {
      // Guards against double-submit — the confirm button is disabled while
      // confirming, but this mirrors the same guard submitForm() uses.
      if (this.confirmingIngredientUnitConversionImport) return;
      if (!this.pendingIngredientUnitConversionFile) return;

      this.confirmingIngredientUnitConversionImport = true;
      try {
        const formData = new FormData();
        formData.append("file", this.pendingIngredientUnitConversionFile);
        const { data } = await this.$http.post(
          "ingredient_unit_conversion_import",
          formData
        );
        if (data.status) {
          this.pendingIngredientUnitConversionFile = null;
          this.ingredientUnitConversionCompareDialog = false;
          await this.showIngredientUnitConversionImportResult(data.data);
        }
      } finally {
        this.confirmingIngredientUnitConversionImport = false;
      }
    },
    async showIngredientUnitConversionImportResult(result) {
      const created = result.created ?? 0;
      const updated = result.updated ?? 0;
      const ingredients = result.ingredients || [];

      const listItemsHtml = ingredients
        .map((ingredient) => {
          const conversionCount = (ingredient.unit_conversions || []).length;
          return `<li>${this.escapeHtml(ingredient.name_la)} (${this.escapeHtml(
            ingredient.unit_name || ""
          )}) — ${this.$t("unit_conversion")}: ${conversionCount}</li>`;
        })
        .join("");

      await this.$swal.fire({
        icon: "success",
        title: this.$t("import_data"),
        width: 560,
        customClass: { popup: "import-summary-popup" },
        html: `
          <div class="import-summary import-summary-scroll">
            <div class="import-summary-stats">
              <div class="import-summary-stat import-summary-stat--created">
                <div class="import-summary-stat-value">${created}</div>
                <div class="import-summary-stat-label">${this.$t(
                  "created"
                )}</div>
              </div>
              <div class="import-summary-stat import-summary-stat--updated">
                <div class="import-summary-stat-value">${updated}</div>
                <div class="import-summary-stat-label">${this.$t(
                  "updated"
                )}</div>
              </div>
            </div>
            <div class="import-summary-new-items">
              <div class="import-summary-new-items-block">
                <div class="import-summary-new-items-title">${this.$t(
                  "ingredient"
                )}</div>
                <ul>${listItemsHtml}</ul>
              </div>
            </div>
          </div>
        `,
        confirmButtonColor: "#438EF2",
      });
    },
    /**
     * Shows the per-product create/update result from product_recipe_import
     * — this endpoint only ever UPDATES an already-existing product (see
     * ProductRecipeImportController), so "created"/"updated" here is about
     * each product's OPTIONS and VARIANTS, not the product row itself.
     */
    async showProductRecipeImportResult(result) {
      const products = result.products || [];
      const MAX_VARIANT_ROWS_SHOWN = 50;

      const productsHtml = products
        .map((product) => {
          const variants = product.variants || [];
          const shown = variants.slice(0, MAX_VARIANT_ROWS_SHOWN);
          const remaining = variants.length - shown.length;

          const variantRowsHtml = shown
            .map((variant) => {
              const actionClass =
                variant.action === "created"
                  ? "import-summary-stat--created"
                  : "import-summary-stat--updated";
              const actionLabel =
                variant.action === "created"
                  ? this.$t("created")
                  : this.$t("updated");
              const label = this.escapeHtml(
                variant.label || variant.sku || ""
              );
              return `
                <tr>
                  <td class="import-summary-identifier-cell">${label}</td>
                  <td>
                    <span class="import-summary-chip ${actionClass}">${actionLabel}</span>
                  </td>
                  <td class="import-summary-row-cell">${
                    variant.recipe_count ?? 0
                  }</td>
                </tr>`;
            })
            .join("");

          let moreRowsNoteHtml = "";
          if (remaining > 0) {
            moreRowsNoteHtml = `<div class="import-summary-more">${this.escapeHtml(
              this.$t("and_more_rows", { count: remaining })
            )}</div>`;
          }

          const variantsTableHtml = variants.length
            ? `
              <div class="import-summary-table-wrap">
                <table class="import-summary-table">
                  <thead>
                    <tr>
                      <th scope="col">${this.escapeHtml(this.$t("option"))}</th>
                      <th scope="col">${this.escapeHtml(this.$t("status"))}</th>
                      <th scope="col" class="import-summary-row-cell">${this.escapeHtml(
                        this.$t("ingredient")
                      )}</th>
                    </tr>
                  </thead>
                  <tbody>${variantRowsHtml}</tbody>
                </table>
              </div>
              ${moreRowsNoteHtml}
            `
            : "";

          const warningHtml = product.warning
            ? `<div class="import-summary-warning">⚠ ${this.$t(
                product.warning
              )}</div>`
            : "";

          return `
            <div class="import-summary-new-items-block">
              <div class="import-summary-new-items-title">
                ${this.escapeHtml(product.product_code)} — ${this.escapeHtml(
            product.product_name || ""
          )}
              </div>
              <div class="import-summary-stats import-summary-stats--stock">
                <div class="import-summary-stat import-summary-stat--created">
                  <div class="import-summary-stat-value">+${
                    product.options_created ?? 0
                  }</div>
                  <div class="import-summary-stat-label">${this.escapeHtml(
                    this.$t("option")
                  )}</div>
                </div>
                <div class="import-summary-stat import-summary-stat--updated">
                  <div class="import-summary-stat-value">${
                    product.options_updated ?? 0
                  }</div>
                  <div class="import-summary-stat-label">${this.escapeHtml(
                    this.$t("updated")
                  )}</div>
                </div>
                <div class="import-summary-stat import-summary-stat--created">
                  <div class="import-summary-stat-value">+${
                    product.variants_created ?? 0
                  }</div>
                  <div class="import-summary-stat-label">SKU</div>
                </div>
                <div class="import-summary-stat import-summary-stat--updated">
                  <div class="import-summary-stat-value">${
                    product.variants_updated ?? 0
                  }</div>
                  <div class="import-summary-stat-label">SKU ${this.escapeHtml(
                    this.$t("updated")
                  )}</div>
                </div>
              </div>
              ${warningHtml}
              ${variantsTableHtml}
            </div>
          `;
        })
        .join("");

      await this.$swal.fire({
        icon: "success",
        title: this.$t("import_data"),
        width: 720,
        html: `<div class="import-summary import-summary-scroll">${productsHtml}</div>`,
        confirmButtonColor: "#438EF2",
      });
    },
    /**
     * Best-effort store_id / branch_id for Ingredient Stock import requests,
     * so the server can scope created/updated ingredients to the current
     * Inventory (store/branch) — the same scoping IngredientStockFormat.php
     * already reads for the export ($this->request->store_id / branch_id).
     *
     * This file has nothing else that reads store/branch context yet, so
     * this uses a best-effort, multi-source pattern: try the route query,
     * then a few common Vuex state shapes, then localStorage — and return
     * undefined (never a made-up value) if none of them resolve. If your
     * project keeps store/branch somewhere else, adjust the candidates
     * below to match.
     */
    currentStoreBranchIds() {
      const pick = (...candidates) => {
        for (const v of candidates) {
          if (v !== undefined && v !== null && v !== "") return v;
        }
        return undefined;
      };
      const dig = (obj, path) => {
        try {
          return path
            .split(".")
            .reduce((o, k) => (o === null || o === undefined ? o : o[k]), obj);
        } catch (e) {
          return undefined;
        }
      };
      const query = (this.$route && this.$route.query) || {};
      const params = (this.$route && this.$route.params) || {};
      let state = {};
      try {
        state = (this.$store && this.$store.state) || {};
      } catch (e) {
        /* no store */
      }
      const readStorage = (storage) => (key) => {
        try {
          return storage.getItem(key);
        } catch (e) {
          return undefined;
        }
      };
      const ls = readStorage(window.localStorage);
      const ss = readStorage(window.sessionStorage);

      // vuex-persistedstate (and similar plugins) commonly persist the
      // whole Vuex store as one JSON blob under a single key — most often
      // "vuex". Parse it once and dig through the same shapes as `state`
      // above, in case the live store isn't reachable from here but the
      // persisted copy is.
      let persisted = {};
      try {
        const raw = ls("vuex") || ss("vuex");
        persisted = raw ? JSON.parse(raw) : {};
      } catch {
        // intentionally ignored: malformed/missing persisted state just
        // means we fall back to the other lookup sources below.
        persisted = {};
      }

      const storeId = pick(
        query.store_id,
        query.storeId,
        params.store_id,
        params.storeId,
        state.store_id,
        state.storeId,
        state.store && state.store.id,
        state.auth && state.auth.store_id,
        state.user && state.user.store_id,
        dig(persisted, "auth.user.store_id"),
        dig(persisted, "auth.store_id"),
        dig(persisted, "user.store_id"),
        dig(persisted, "store.id"),
        dig(persisted, "store_id"),
        ls("store_id"),
        ls("storeId"),
        ss("store_id"),
        ss("storeId")
      );
      const branchId = pick(
        query.branch_id,
        query.branchId,
        params.branch_id,
        params.branchId,
        state.branch_id,
        state.branchId,
        state.branch && state.branch.id,
        state.auth && state.auth.branch_id,
        state.user && state.user.branch_id,
        dig(persisted, "auth.user.branch_id"),
        dig(persisted, "auth.branch_id"),
        dig(persisted, "user.branch_id"),
        dig(persisted, "branch.id"),
        dig(persisted, "branch_id"),
        ls("branch_id"),
        ls("branchId"),
        ss("branch_id"),
        ss("branchId")
      );

      // Debug aid: if neither id resolved, say so once in the console with
      // exactly what was checked, instead of silently posting without them
      // (which is what produced the payload with only `file` in it).
      if (storeId === undefined || branchId === undefined) {
        // eslint-disable-next-line no-console
        console.warn(
          "[ingredient_stock import] could not resolve store_id/branch_id from " +
            "route query/params, Vuex state, persisted \"vuex\" storage, or " +
            "plain localStorage/sessionStorage keys. The request will be sent " +
            "without them. Check how the rest of the app reads the current " +
            "store/branch and adjust currentStoreBranchIds() to match.",
          { storeId, branchId }
        );
      }

      return { storeId, branchId };
    },
    /**
     * Ingredient Stock only (Product Stock behaviour is untouched) — adds
     * store_id/branch_id to a FormData payload when they can be resolved.
     */
    appendStoreBranch(formData) {
      if (this.select.api !== "ingredient_stock") return;
      const { storeId, branchId } = this.currentStoreBranchIds();
      if (storeId !== undefined) formData.append("store_id", storeId);
      if (branchId !== undefined) formData.append("branch_id", branchId);
    },
    /**
     * Stock Import only (Product Stock and Ingredient Stock both use this)
     * — defensive fallback for a backend that still reports an unmatched
     * row as a failed row (`error: "ingredient_not_found"` /
     * "product_not_found"`) instead of the current shape (`status: "new"`,
     * no error). Not actually an invalid row either way: the row is simply
     * introducing a brand-new item, so the preview should show it like any
     * other row instead of flagging it red.
     */
    isNotFoundRow(row) {
      const error = String((row && row.error) || "").trim().toLowerCase();
      return NOT_FOUND_ERROR_CODES.has(error);
    },
    /**
     * A row whose product/ingredient WAS matched, but whose stock is
     * managed elsewhere (product variations/bundle/BOM — Ingredient Stock
     * never sends this). This is not bad data either: the item is real and
     * the comparison numbers (current/new/result) are real, it just won't
     * be written on Confirm. It must not read as a failed row.
     */
    isManagedSeparatelyRow(row) {
      const error = String((row && row.error) || "").trim().toLowerCase();
      return error === "product_stock_managed_separately";
    },
    /**
     * current + new, treating a missing side as 0 — except when BOTH sides
     * are missing, where there is nothing to compute and null (→ "-") is
     * correct. Mirrors ExcelController::stockRowResult() on the backend;
     * used here only as a fallback when the backend didn't already supply
     * `result` for a row this file reclassifies.
     */
    computeFallbackResult(current, newQty) {
      const hasCurrent = current !== null && current !== undefined && current !== "";
      const hasNew = newQty !== null && newQty !== undefined && newQty !== "";
      if (!hasCurrent && !hasNew) return null;

      const numericCurrent = hasCurrent ? Number(current) : 0;
      const numericNew = hasNew ? Number(newQty) : 0;
      if (!Number.isFinite(numericCurrent) || !Number.isFinite(numericNew)) return null;

      return Math.round((numericCurrent + numericNew) * 1000) / 1000;
    },
    /**
     * Rewrites a "managed separately" row into a normal, non-error
     * comparison row. current/new are left exactly as the backend sent
     * them (the entity is real and already matched — nothing to fall back
     * to); only status/error are cleared, and result is filled in with
     * current+new when the backend didn't already provide one.
     */
    toManagedSeparatelyDisplayRow(row) {
      const result = row.result !== undefined && row.result !== null
        ? row.result
        : this.computeFallbackResult(row.current, row.new);
      return {
        ...row,
        result,
        status: "ready",
        error: "",
      };
    },
    /**
     * Rewrites a single "not found" row (old backend shape) into a normal,
     * importable "new item" row: current = null (displayed as "-"), result
     * = new (numeric when possible), status "new", error cleared. Code/name
     * are left untouched — those already come straight from the Excel row
     * on the backend response.
     */
    toNewItemRow(row) {
      const numericNew = Number(String(row.new).replace(/[, ]/g, ""));
      const hasNew = row.new !== null && row.new !== undefined && row.new !== "";
      const result = hasNew && Number.isFinite(numericNew) ? numericNew : null;
      return {
        ...row,
        current: null,
        new: hasNew ? row.new : null,
        result,
        status: "new",
        error: "",
      };
    },
    /**
     * Walks a preview response and turns two kinds of non-error rows into
     * normal display rows, so neither ever renders red or shows an error
     * message:
     *   - a "not found" row (old backend shape, see isNotFoundRow) becomes
     *     a normal new-item row via toNewItemRow().
     *   - a "product_stock_managed_separately" row (a real, matched product
     *     whose stock lives elsewhere — variations/bundle/BOM) becomes a
     *     normal ready row via toManagedSeparatelyDisplayRow(), keeping its
     *     real current/new/result.
     * Either way the summary's failed_count shrinks by the same amount so
     * the red "some rows are invalid" banner and the Confirm-button gating
     * both reflect the corrected picture. Real validation failures (a
     * genuinely bad stock value, an unreadable file, a server error) are
     * untouched — this only ever reclassifies these two specific, known-safe
     * cases.
     *
     * A no-op for the demo/offline preview (`isDemo: true`).
     */
    reclassifyMissingStockRows(preview) {
      if (!preview || preview.isDemo || !Array.isArray(preview.rows)) {
        return preview;
      }

      let reclassified = 0;
      const rows = preview.rows.map((row) => {
        if (this.isNotFoundRow(row)) {
          reclassified += 1;
          return this.toNewItemRow(row);
        }
        if (this.isManagedSeparatelyRow(row)) {
          reclassified += 1;
          return this.toManagedSeparatelyDisplayRow(row);
        }
        return row;
      });

      if (!reclassified) return preview;

      const summary = { ...preview.summary };
      const previousFailed = Number(summary.failed_count || 0);
      summary.failed_count = Math.max(0, previousFailed - reclassified);
      summary.will_update = Number(summary.will_update || 0) + reclassified;

      return { ...preview, rows, summary };
    },
    async previewStockImport() {
      if (!this.file || this.previewing || this.committing) return;
      this.previewing = true;
      this.comparePreview = null;
      try {
        const formData = new FormData();
        formData.append("file", this.file);
        const res = await this.$http.post(
          `${this.select.api}_preview`, formData,
          { validateStatus: () => true }
        );

        // Both Product Stock and Ingredient Stock now have a real backend
        // preview endpoint (ExcelController::buildStockPreview()), so this
        // no longer falls back to a client-side "demo" compare when the
        // endpoint 404s — a preview that silently degrades into a guess
        // would risk showing the user numbers the backend never confirmed.
        // If this 404s, the route for `${api}_preview` isn't wired up on
        // this backend yet — surface that clearly instead of faking a
        // preview.
        const data = this.stockResponseData(res);
        let preview = data.data;
        if (!preview || !preview.token || !Array.isArray(preview.rows)) {
          throw new Error(this.$t("preview_failed"));
        }

        preview = this.reclassifyMissingStockRows(preview);
        this.comparePreview = preview;
        this.dialog = false;
        this.compareDialog = true;
      } catch (err) {
        await this.showRequestError(this.$t("preview_failed"), this.describeError(err));
      } finally {
        this.previewing = false;
      }
    },
    stockResponseData(res) {
      if (!res || !res.data) throw new Error(this.$t("empty_import_response"));
      const data = res.data;
      if (res.status >= 400 || ![true, 1, "1", "true"].includes(data.status)) {
        throw new Error(
          this.translateServerMessage(data.message) || this.$t("server_rejected_file")
        );
      }
      return data;
    },

    /**
     * Client-side parse, so the compare UI has real rows while the backend
     * preview endpoint doesn't exist yet.
     *
     * Handles .xlsx / .xls (through the parser loaded in loadXlsx()) as well
     * as CSV. The previous version bailed out on anything that wasn't .csv,
     * which is why picking the Excel template produced a dialog of zeros with
     * no explanation.
     *
     * The workbook's lookup tab is parsed alongside the form sheet and handed
     * to compareFromTable(), which uses it to fill in a `code` the form sheet
     * doesn't carry.
     */
    async buildDemoCompare() {
      if (!this.file) return null;

      const isCsv = /\.csv$/i.test(this.file.name || "");
      let table = null;
      let lookupTable = null;
      let parserMissing = false;

      try {
        if (isCsv) {
          // A CSV is one sheet by definition — no lookup tab to fall back on.
          table = await this.readCsvTable();
        } else {
          const XLSX = await this.loadXlsx();
          if (!XLSX) {
            parserMissing = true;
          } else {
            const tables = await this.readWorkbookTables(XLSX);
            table = tables.main;
            lookupTable = tables.lookup;
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[import] preview parse failed", e);
        table = null;
      }

      if (parserMissing) return this.emptyDemoCompare({ parserMissing: true });
      if (!table || table.length < 2) return this.emptyDemoCompare({});

      return this.compareFromTable(table, this.buildLookupIndex(lookupTable));
    },

    /** A preview with nothing in it — the shape the dialog expects. */
    emptyDemoCompare(flags) {
      return {
        isDemo: true,
        parserMissing: false,
        token: null,
        summary: {
          total_rows: 0,
          will_update: 0,
          unchanged: 0,
          failed_count: 0,
        },
        rows: [],
        ...flags,
      };
    },

    /**
     * window.XLSX if something else already loaded it, otherwise fetch it
     * once. The promise is cached so two previews can't inject two scripts;
     * a failed load clears the cache so a later attempt can retry.
     */
    loadXlsx() {
      if (window.XLSX && window.XLSX.read) return Promise.resolve(window.XLSX);
      if (this.xlsxPromise) return this.xlsxPromise;

      this.xlsxPromise = new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = XLSX_CDN;
        script.async = true;
        script.onload = () =>
          resolve(window.XLSX && window.XLSX.read ? window.XLSX : null);
        script.onerror = () => {
          this.xlsxPromise = null;
          resolve(null);
        };
        document.head.appendChild(script);
      });

      return this.xlsxPromise;
    },

    /**
     * Both sheets as arrays-of-cells, first row = header.
     *
     * The form sheet is picked by NAME, not by index: the stock templates ship
     * a second, deliberately visible lookup sheet, and reading whichever sheet
     * comes first would count that reference list instead of the form the
     * customer filled in.
     *
     * The lookup sheet is returned alongside it — that is where a `code` lives
     * when the form sheet hasn't got one.
     */
    async readWorkbookTables(XLSX) {
      const buffer = await this.readFileArrayBuffer();
      const workbook = XLSX.read(new Uint8Array(buffer), { type: "array" });
      const names = workbook.SheetNames || [];

      const toTable = (name) => {
        const sheet = name ? workbook.Sheets[name] : null;
        if (!sheet) return null;
        return XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          blankrows: false,
          defval: "",
          raw: true,
        });
      };

      const mainName = names.includes(TEMPLATE_SHEET)
        ? TEMPLATE_SHEET
        : names[0];

      let lookupName = null;
      for (const sheetName of LOOKUP_SHEETS) {
        if (names.includes(sheetName)) {
          lookupName = sheetName;
          break;
        }
      }
      // A renamed lookup tab still works: take whatever isn't the form sheet.
      if (!lookupName) {
        lookupName = names.find((n) => n !== mainName) || null;
      }

      return { main: toTable(mainName), lookup: toTable(lookupName) };
    },

    async readCsvTable() {
      const text = await this.readFileText();
      return text
        .split(/\r?\n/)
        .filter((line) => line.trim().length > 0)
        .map((line) =>
          line
            .split(/[,;\t]/)
            .map((cell) => cell.trim().replace(/^"(.*)"$/, "$1"))
        );
    },

    readFileText() {
      return this.file.text();
    },

    readFileArrayBuffer() {
      return this.file.arrayBuffer();
    },

    /**
     * A cell that still holds an Excel formula. The template used to fill its
     * hidden code/id columns with `=IF($B2=…)`, and PhpSpreadsheet's
     * getValue() hands the importer that formula string verbatim — which is
     * how a file could come back as 1,000 rows of `id_must_be_integer`.
     * Treat it as "not filled in" rather than as an identifier.
     */
    isFormula(value) {
      return typeof value === "string" && value.charAt(0) === "=";
    },

    /** Trimmed text for cols[i], or "" for missing/formula cells. */
    cellText(cols, i) {
      if (i < 0) return "";
      const raw = cols[i];
      if (raw === null || raw === undefined) return "";
      if (this.isFormula(raw)) return "";
      return String(raw).trim();
    },

    /** Normalised match key: trimmed, lower-cased, whitespace collapsed. */
    lookupKey(text) {
      return String(text === null || text === undefined ? "" : text)
        .trim()
        .replace(/\s+/g, " ")
        .toLowerCase();
    },

    /**
     * Normalize a raw header row into trimmed, lowercased strings, tolerant
     * of null/undefined cells. Shared by buildLookupIndex and
     * compareFromTable so their header handling can't drift apart.
     */
    normalizeHeaderRow(row) {
      return (row || []).map((cell) =>
        String(cell === null || cell === undefined ? "" : cell)
          .trim()
          .toLowerCase()
      );
    },

    /**
     * Find the index of the first header cell that matches one of the given
     * aliases. Shared by buildLookupIndex and compareFromTable.
     */
    findColumnIndex(header, aliases) {
      return header.findIndex((h) => aliases.includes(h));
    },

    /**
     * Index the lookup sheet so a main-sheet row missing its code can borrow
     * one: by id first (exact), then by the label or name the customer picked.
     *
     * This is what puts a value in the Code column of the compare dialog. The
     * form sheet legitimately has no `code` column whenever nothing in the
     * branch has a code (the exporter drops the column rather than shipping it
     * blank, which the importer rejects), and a customer is free to delete the
     * hidden columns before uploading — in both cases the visible reference
     * tab still knows the code.
     *
     * Built on a null-prototype object: an item literally named "constructor"
     * would otherwise match Object.prototype and hand back a function.
     */
    buildLookupIndex(table) {
      if (!table || table.length < 2) return null;

      const header = this.normalizeHeaderRow(table[0]);
      const findCol = (aliases) => this.findColumnIndex(header, aliases);

      const idx = {
        code: findCol(LOOKUP_ALIASES.code),
        name: findCol(LOOKUP_ALIASES.name),
        label: findCol(LOOKUP_ALIASES.label),
        id: findCol(LOOKUP_ALIASES.id),
      };

      // Nothing recognised: fall back to the template's own column order,
      // code | name | label | id.
      if (idx.code < 0 && idx.name < 0 && idx.label < 0 && idx.id < 0) {
        idx.code = 0;
        idx.name = 1;
        idx.label = 2;
        idx.id = 3;
      }

      const at = (cols, i) => this.cellText(cols, i);

      const byId = Object.create(null);
      const byText = Object.create(null);
      let found = 0;

      for (let i = 1; i < table.length; i++) {
        const cols = table[i] || [];
        const entry = {
          code: at(cols, idx.code),
          name: at(cols, idx.name),
          label: at(cols, idx.label),
          id: at(cols, idx.id),
        };
        if (!entry.code && !entry.name && !entry.label && !entry.id) continue;

        found++;
        if (entry.id && byId[entry.id] === undefined) byId[entry.id] = entry;

        // First writer wins, so a duplicate name can't shadow the row above it.
        [entry.label, entry.name].forEach((text) => {
          const key = this.lookupKey(text);
          if (key && byText[key] === undefined) byText[key] = entry;
        });
      }

      return found ? { byId, byText } : null;
    },

    /** The lookup row this main-sheet row refers to, or null. */
    resolveLookupEntry(lookup, id, code, name) {
      if (!lookup) return null;

      const idKey = String(id === null || id === undefined ? "" : id).trim();
      if (idKey && lookup.byId[idKey]) return lookup.byId[idKey];

      // The picked label matches column C; the bare name matches column B.
      const candidates = [name, code];
      for (const candidate of candidates) {
        const key = this.lookupKey(candidate);
        if (key && lookup.byText[key]) return lookup.byText[key];
      }

      return null;
    },

    /**
     * Header-driven mapping + row validation.
     *
     * Positional guessing runs ONLY when not one header was recognised. With
     * a recognised header, a column that isn't in the file stays empty rather
     * than being filled from its neighbour — that fallback is what used to
     * print the quantity under "Code".
     *
     * `name` is deliberately read from TWO possible sources instead of one
     * column index. The current template's `main` sheet ships a HIDDEN
     * `name` column that only ever carries a value on the two shipped
     * example rows (see the PHP exporter's writeExampleRows()) — every row a
     * customer adds by hand leaves it blank. The value they actually typed or
     * picked lives in `select_ingredient` / `select_product` instead.
     *
     * The old code did `header.includes("name") ? header.indexOf("name") :
     * findCol(...)`, which — once the template shipped a hidden `name` header
     * at all — locked onto that mostly-blank column for EVERY row, including
     * the ones the customer filled in. That's why a row picked from the
     * dropdown, or a brand-new name typed into it, came back as
     * `identifier_is_required`: the picker text was never read.
     *
     * Anything the form sheet leaves blank is then filled in from the lookup
     * tab, which is how the Code column stops printing a dash on a file whose
     * `main` sheet has no code column at all.
     *
     * Rows that can't be imported are KEPT and flagged instead of being
     * dropped, so the compare dialog shows `identifier_is_required` before
     * the upload rather than the result dialog showing it afterwards.
     */
    /**
     * Resolve the main-sheet column indexes from its header row, extracted
     * out of compareFromTable to keep that function's complexity down.
     */
    resolveMainSheetColumns(header) {
      const findCol = (aliases) => this.findColumnIndex(header, aliases);

      const idx = {
        code: findCol(COLUMN_ALIASES.code),
        // Hidden hint column — filled only on the two shipped example rows.
        hiddenName: header.includes("name") ? header.indexOf("name") : -1,
        // The picker column the customer actually fills in for every row.
        picker: findCol(PICKER_COLUMN_ALIASES),
        qty: findCol(COLUMN_ALIASES.qty),
        id: findCol(COLUMN_ALIASES.id),
      };
      // Older templates that ship neither a hidden `name` column nor a
      // picker header — fall back to whatever name-ish header exists.
      if (idx.hiddenName < 0 && idx.picker < 0) {
        idx.hiddenName = findCol(COLUMN_ALIASES.name);
      }
      const recognised =
        idx.code >= 0 || idx.hiddenName >= 0 || idx.picker >= 0 ||
        idx.qty >= 0 || idx.id >= 0;

      return { idx, recognised };
    },

    /**
     * Pull code/name/qty/id out of one row's raw columns, resolving against
     * the lookup sheet where the main sheet leaves a value blank. Extracted
     * out of compareFromTable to keep that function's complexity down.
     */
    extractRowFields(cols, idx, recognised, cell, lookup) {
      const rawCode = recognised ? cell(cols, idx.code) : cell(cols, 0);
      const rawHiddenName = recognised ? cell(cols, idx.hiddenName) : "";
      const rawPicker = recognised ? cell(cols, idx.picker) : cell(cols, 2);
      // Prefer the hidden hint column when it's there (the example rows);
      // otherwise fall back to whatever the customer actually typed/picked.
      const rawName = rawHiddenName || rawPicker;
      const qty = recognised ? cell(cols, idx.qty) : cell(cols, 1);
      const rawId = recognised ? cell(cols, idx.id) : "";

      // The form sheet is allowed to carry nothing but a picked label.
      // Borrow whatever the reference tab knows about this item rather than
      // printing a dash in the Code column.
      const entry = this.resolveLookupEntry(lookup, rawId, rawCode, rawName);
      const code = rawCode || (entry ? entry.code : "") || "";
      const id = rawId || (entry ? entry.id : "") || "";
      // Prefer the CLEAN name resolved from the reference sheet (e.g.
      // "Rice") over the raw picker text, which can carry a "<code> - "
      // prefix for an existing item picked from the dropdown. Only fall
      // back to the raw text when nothing matched — exactly the
      // brand-new-ingredient case, where it's already a plain name.
      const name = (entry && (entry.name || entry.label)) || rawName || "";

      return { code, id, name, qty };
    },

    /**
     * Decide a row's status/error from its resolved identifier and quantity.
     * Extracted out of compareFromTable to keep that function's complexity
     * down. Same rule the importer applies: a row needs SOMETHING that names
     * an item. Empty identifier columns are the whole `identifier_is_required`
     * bug — they used to be skipped silently here and only blew up on the
     * server, after the user had already pressed Confirm.
     */
    evaluateRowStatus(identifier, qty) {
      const numericQty = Number(String(qty).replace(/[, ]/g, ""));
      if (!identifier) {
        return { status: "error", error: "identifier_is_required" };
      }
      if (qty === "") {
        return { status: "error", error: "stock_is_required" };
      }
      if (!Number.isFinite(numericQty)) {
        return { status: "error", error: "stock_must_be_number" };
      }
      return { status: "ok", error: "" };
    },

    compareFromTable(table, lookup) {
      const header = this.normalizeHeaderRow(table[0]);
      const { idx, recognised } = this.resolveMainSheetColumns(header);
      const cell = (cols, i) => this.cellText(cols, i);

      const rows = [];
      const body = table.slice(1);
      let okCount = 0;
      let failedCount = 0;

      for (let i = 0; i < body.length; i++) {
        const cols = body[i] || [];
        const isBlank = !cols.some(
          (c) =>
            String(c === null || c === undefined ? "" : c).trim().length > 0
        );
        if (isBlank) continue;

        const { code, id, name, qty } = this.extractRowFields(
          cols,
          idx,
          recognised,
          cell,
          lookup
        );
        const identifier = id || code || name;
        const { status, error } = this.evaluateRowStatus(identifier, qty);

        if (status === "error") failedCount++;
        else okCount++;

        rows.push({
          row: i + 2,
          // Left RAW, not "-": the template decides how to render an empty
          // code. Writing "-" in here made `row.code || row.identifier` in the
          // template dead code, because "-" is truthy — the fallback could
          // never fire.
          code,
          identifier,
          name: name || "-",
          current: "-",
          new: qty === "" ? "-" : qty,
          status,
          error,
        });


      }

      if (!rows.length) return this.emptyDemoCompare({});

      return {
        isDemo: true,
        parserMissing: false,
        token: null,
        summary: {
          total_rows: okCount + failedCount,
          // Nothing to compare against yet, so every VALID row is reported as
          // an update. `unchanged` becomes meaningful the moment the backend
          // preview endpoint lands.
          will_update: okCount,
          unchanged: 0,
          failed_count: failedCount,
        },
        rows,
      };
    },

    async directStockImportFallback() {
      if (!this.file || this.committing) return;
      this.committing = true;
      try {
        const formData = new FormData();
        formData.append("file", this.file);
        const res = await this.$http.post(
          `${this.select.api}_import`, formData,
          { validateStatus: () => true }
        );
        // Throws (and shows the error dialog) on a genuine HTTP-level
        // rejection; on success `data.data` is the server's real row-level
        // result — that, not the client preview, is what gets shown.
        const data = this.stockResponseData(res);
        this.dialog = false;
        await this.showStockImportSummary(data.data);
      } catch (err) {
        await this.showRequestError(this.$t("import_failed"), this.describeError(err));
      } finally {
        this.resetImportFile();
        this.committing = false;
      }
    },
    // Backend abort() codes (e.g. "missing_required_sheet",
    // "cannot_import_while_errors_remain") are raw snake_case strings, not
    // user-facing text — translate them when a matching locale key exists,
    // otherwise fall back to showing the code as-is so nothing is silently
    // swallowed. Shared by describeError() (network/5xx failures, thrown)
    // and the plain `data.status === false` branches (4xx responses that
    // resolve normally instead of throwing).
    //
    // Some codes carry a parameter after a "|", e.g.
    // "missing_required_header|stock" (ExcelController::readStockRows() —
    // the uploaded file's heading row is missing the stock column). Split
    // that off and pass it through as {field} so the base code's
    // translation can interpolate it — translating the field name itself
    // too (e.g. "stock" -> "ສະຕັອກ") when a matching key exists, so the
    // whole message reads in one language instead of mixing in a raw
    // English column name.
    translateServerMessage(message) {
      if (!message) return message;

      const [code, param] = String(message).split("|");
      if (param !== undefined) {
        if (this.$te(code)) {
          const field = this.$te(param) ? this.$t(param) : param;
          return this.$t(code, { field });
        }
        return message;
      }

      return this.$te(message) ? this.$t(message) : message;
    },
    describeError(err) {
      const response = (err && err.response) || {};
      const status = response.status;
      const serverMessage = response.data && response.data.message;
      if (status === 404) {
        return this.$t("endpoint_not_found", {
          url: (err && err.config && err.config.url) || "",
        });
      }
      if (serverMessage) {
        return `${this.translateServerMessage(serverMessage)} (HTTP ${status || "?"})`;
      }
      return (err && err.message) || this.$t("unknown_error");
    },
    async showRequestError(title, text) {
      await this.$swal.fire({
        icon: "error",
        title,
        // multi-line stack/file info needs to keep its newlines and wrap
        html: `<pre class="import-error-text">${this.escapeHtml(text)}</pre>`,
        width: 640,
        confirmButtonColor: "#438EF2",
        confirmButtonText: this.confirmLabel(),
      });
    },
    async confirmStockImport() {
      if (!this.comparePreview || this.committing) return;
      if (!this.canConfirmStockImport) return;

      // TEMPORARY: a demo preview (built client-side because the backend
      // has no preview endpoint yet) has no real token to commit — fall
      // back to the plain one-shot import instead. Delete this branch once
      // buildDemoCompare()/the 404 fallback above is removed.
      if (this.comparePreview.isDemo) {
        this.compareDialog = false;
        this.comparePreview = null;
        await this.directStockImportFallback();
        return;
      }

      this.committing = true;
      try {
        const commitPayload = { token: this.comparePreview.token };
        const res = await this.$http.post(
          `${this.select.api}_import`,
          commitPayload,
          { validateStatus: () => true }
        );
        const data = this.stockResponseData(res);
        if (data.status) {
          this.compareDialog = false;
          this.resetImportFile();
          this.comparePreview = null;
          // The server's real row-level result — total/updated/created/
          // unchanged/failed, and the real failed-row errors — never the
          // client preview's own counts.
          await this.showStockImportSummary(data.data);
        } else {
          await this.showRequestError(
            this.$t("update_failed"),
            data.message || this.$t("server_rejected_update")
          );
        }
      } catch (err) {
        await this.showRequestError(
          this.$t("update_failed"),
          this.describeError(err)
        );
      } finally {
        this.committing = false;
      }
    },
    cancelStockCompare() {
      this.compareDialog = false;
      this.comparePreview = null;
      this.resetImportFile();
    },
    // Real backend preview rows use status "failed" (see
    // ExcelController::IngredientStockPreview()); the client-side demo
    // compare (Product Stock only) uses status "error". Both mean the same
    // thing to the UI: this row won't be imported. (Ingredient Stock rows
    // that only failed with `ingredient_not_found` are reclassified to
    // status "ok" before they ever reach this check — see
    // reclassifyMissingIngredientRows() above.)
    isFailedRow(row) {
      return row.status === "error" || row.status === "failed";
    },
    // "-" only when the value genuinely doesn't exist (e.g. current stock
    // for a row whose ingredient wasn't found) — never for a real 0.
    qtyOrDash(value) {
      return value === null || value === undefined ? "-" : value;
    },
    /**
     * A raw "<code> - <name>" (or "<code> - <name> - <anything>") label as
     * it appears in the picker column / the picked-from-dropdown text. Only
     * used as a last-resort source for name/code when neither was sent as
     * its own field — never preferred over an explicit field.
     */
    splitCodeNameLabel(label) {
      const text = String(label ?? "").trim();
      if (!text) return { code: "", name: "" };

      // Normalize runs of whitespace first, then split on the plain " - "
      // literal — avoids a regex with two adjacent unbounded \s+ quantifiers
      // (super-linear backtracking risk) while still tolerating extra
      // spaces around the separator.
      const parts = text.replace(/\s+/g, " ").split(" - ");
      if (parts.length < 2) {
        // No " - " separator: nothing to split, treat the whole thing as
        // the name (this is the shape a code-less item's label takes).
        return { code: "", name: text };
      }

      return { code: parts[0].trim(), name: parts.slice(1).join(" - ").trim() };
    },
    /**
     * Maps the app's active locale ($i18n.locale: "la"/"en"/"th"/"ch"/"cn")
     * to the matching name_* column the backend now sends on every preview
     * row (see ExcelController::stockEntityNameFields()). A viewer using
     * the Chinese UI was seeing whatever language happened to win
     * `row.name`'s fixed name_la > name > name_en > name_th > name_cn
     * priority order (e.g. "testbundlestock1" from name_en) instead of the
     * product's actual name_cn — because the preview response used to carry
     * only that one resolved string, with nothing for the frontend to
     * prefer once it knows which language the viewer is actually using.
     */
    localeNameField() {
      const locale = String(this.$i18n && this.$i18n.locale).toLowerCase();
      if (locale === "cn" || locale === "ch") return "name_cn";
      if (locale === "th") return "name_th";
      if (locale === "en") return "name_en";
      return "name_la";
    },
    /**
     * Real product/ingredient name for a preview row. Prefers the column
     * matching the viewer's own UI language (localeNameField()), and only
     * falls through to every other shape a backend response has been seen
     * to use — including the backend's own cross-language best guess,
     * `row.name` — when that language's column is blank. Never turns an
     * already-present name/code into "-" — this only fills in a blank.
     */
    resolveRowName(row) {
      if (!row) return "";

      const direct = [
        row[this.localeNameField()],
        row.name,
        row.product_name,
        row.name_la,
        row.name_en,
        row.name_th,
        row.name_cn,
        row.product && row.product[this.localeNameField()],
        row.product && row.product.name,
        row.product && row.product.name_la,
        row.product && row.product.name_en,
        row.ingredient && row.ingredient[this.localeNameField()],
        row.ingredient && row.ingredient.name,
        row.ingredient && row.ingredient.name_la,
      ];
      for (const candidate of direct) {
        const value = candidate === null || candidate === undefined ? "" : String(candidate).trim();
        if (value) return value;
      }

      // Last resort: parse the original selected text, e.g. "202608483 - tum".
      const rawLabel = row.label ?? row.identifier ?? row.select_product
        ?? row.select_ingredient ?? row.product_label ?? row.original ?? "";
      const { name } = this.splitCodeNameLabel(rawLabel);
      return name;
    },
    /**
     * Real product/ingredient code for a preview row — same idea as
     * resolveRowName(), tried first against explicit fields, and only
     * parsed out of a raw "<code> - <name>" label as a last resort.
     */
    resolveRowCode(row) {
      if (!row) return "";

      const direct = [
        row.code,
        row.product_code,
        row.ingredient_code,
        row.sku,
        row.barcode,
        row.product && row.product.code,
        row.ingredient && row.ingredient.code,
      ];
      for (const candidate of direct) {
        const value = candidate === null || candidate === undefined ? "" : String(candidate).trim();
        if (value) return value;
      }

      const rawLabel = row.label ?? row.identifier ?? row.select_product
        ?? row.select_ingredient ?? row.product_label ?? row.original ?? "";
      const { code } = this.splitCodeNameLabel(rawLabel);
      return code;
    },
    diffValue(row) {
      if (row.diff !== undefined && row.diff !== null) return Number(row.diff);
      return Number(row.new) - Number(row.current);
    },
    diffLabel(row) {
      const diff = this.diffValue(row);
      if (Number.isNaN(diff)) return "-";
      return diff > 0 ? `+${diff}` : `${diff}`;
    },
    diffClass(row) {
      const diff = this.diffValue(row);
      if (Number.isNaN(diff) || diff === 0)
        return "import-compare-diff--neutral";
      return diff > 0 ? "import-compare-diff--up" : "import-compare-diff--down";
    },
    escapeHtml(text) {
      const div = document.createElement("div");
      div.textContent = text === undefined || text === null ? "" : text;
      return div.innerHTML;
    },
    // Safe non-negative integer parse, or null when the value is missing/invalid.
    parseRowCount(value) {
      if (value === undefined || value === null || value === "") return null;
      const n = Number(value);
      return Number.isSafeInteger(n) && n >= 0 ? n : null;
    },
    buildStockFailedRowsTable(failedRows, maxRowsShown) {
      const shown = failedRows.slice(0, maxRowsShown);
      const remaining = failedRows.length - shown.length;
      if (shown.length === 0) return "";

      // `identifier_is_required` and friends are error CODES. $t() now has
      // translations for them, so this prints a sentence instead of the key.
      const tableRows = shown
        .map(
          (item) => `
            <tr>
              <td class="import-summary-row-cell">${this.escapeHtml(
                item.row
              )}</td>
              <td class="import-summary-identifier-cell">${this.escapeHtml(
                item.identifier || "-"
              )}</td>
              <td class="import-summary-error-cell">${this.escapeHtml(
                this.$t(item.error)
              )}</td>
            </tr>`
        )
        .join("");

      const moreRowsNote =
        remaining > 0
          ? `<div class="import-summary-more">${this.escapeHtml(
              this.$t("and_more_rows", { count: remaining })
            )}</div>`
          : "";

      return `
              <div class="import-summary-table-wrap">
                <table class="import-summary-table">
                  <thead>
                    <tr>
                      <th scope="col" class="import-summary-row-cell">${this.escapeHtml(
                        this.$t("row")
                      )}</th>
                      <th scope="col" class="import-summary-identifier-cell">${this.escapeHtml(
                        this.$t("code")
                      )}</th>
                      <th scope="col" class="import-summary-error-cell">${this.escapeHtml(
                        this.$t("error")
                      )}</th>
                    </tr>
                  </thead>
                  <tbody>${tableRows}</tbody>
                </table>
              </div>
              ${moreRowsNote}
            `;
    },
    // Which products actually changed and what their stock is now — not
    // just the counts (see ExcelController::commitStockPreview()/
    // importStockLevels(), which now send `updated_rows`/`created_rows`
    // alongside the summary counts).
    buildStockSuccessRowsTable(s, maxRowsShown) {
      const successRows = [
        ...(Array.isArray(s.updated_rows) ? s.updated_rows : []),
        ...(Array.isArray(s.created_rows) ? s.created_rows : []),
      ].sort((a, b) => Number(a.row || 0) - Number(b.row || 0));
      const successShown = successRows.slice(0, maxRowsShown);
      const successRemaining = successRows.length - successShown.length;
      if (successShown.length === 0) return "";

      const successTableRows = successShown
        .map(
          (item) => `
            <tr>
              <td class="import-summary-row-cell">${this.escapeHtml(item.row)}</td>
              <td class="import-summary-identifier-cell">${this.escapeHtml(
                item.code || "-"
              )}</td>
              <td class="import-summary-identifier-cell">${this.escapeHtml(
                item.name || "-"
              )}</td>
              <td class="import-summary-qty-cell">${this.escapeHtml(
                this.qtyOrDash(item.added)
              )}</td>
              <td class="import-summary-qty-cell">${this.escapeHtml(
                this.qtyOrDash(item.stock)
              )}</td>
            </tr>`
        )
        .join("");

      const successMoreRowsNote =
        successRemaining > 0
          ? `<div class="import-summary-more">${this.escapeHtml(
              this.$t("and_more_rows", { count: successRemaining })
            )}</div>`
          : "";

      return `
              <div class="import-summary-table-wrap">
                <table class="import-summary-table import-summary-success-table">
                  <thead>
                    <tr>
                      <th scope="col" class="import-summary-row-cell">${this.escapeHtml(
                        this.$t("row")
                      )}</th>
                      <th scope="col" class="import-summary-identifier-cell">${this.escapeHtml(
                        this.$t("code")
                      )}</th>
                      <th scope="col" class="import-summary-identifier-cell">${this.escapeHtml(
                        this.$t("name")
                      )}</th>
                      <th scope="col" class="import-summary-qty-cell">${this.escapeHtml(
                        this.$t("stock_excel_add")
                      )}</th>
                      <th scope="col" class="import-summary-qty-cell">${this.escapeHtml(
                        this.$t("stock_final")
                      )}</th>
                    </tr>
                  </thead>
                  <tbody>${successTableRows}</tbody>
                </table>
              </div>
              ${successMoreRowsNote}
            `;
    },
    // Product Stock never creates a record on a miss — this tile is only
    // meaningful for imports that can (e.g. Ingredient Stock). Hidden
    // instead of showing a permanent "0 created" tile that never applies.
    buildCreatedStatTile(created) {
      if (created <= 0) return "";
      return `
              <div class="import-summary-stat import-summary-stat--created">
                <div class="import-summary-stat-value">${created}</div>
                <div class="import-summary-stat-label">${this.escapeHtml(
                  this.$t("created")
                )}</div>
              </div>`;
    },
    buildPendingPurchaseBanner(purchaseCode) {
      if (!purchaseCode) return "";
      return `
              <div class="import-summary-pending-banner">
                <svg class="import-summary-pending-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="9"></circle>
                  <polyline points="12 7 12 12 15.5 14"></polyline>
                </svg>
                <span>${this.escapeHtml(
                  this.$t("stock_purchase_pending_banner", { code: purchaseCode })
                )}</span>
              </div>`;
    },
    stockImportResultIcon(hasFailures, totalRows, isPendingPurchase) {
      if (hasFailures) return "warning";
      if (totalRows > 0 && !isPendingPurchase) return "success";
      return "info";
    },
    /**
     * The stock-import result dialog: Total / Created / Updated / Unchanged
     * / Failed, computed from the backend's OWN response — never the
     * client-side preview's counts. Shows the real failed-row errors
     * (translated where we have a string for the error code, the raw code
     * otherwise) when the server rejected any rows.
     *
     * All five cards render unconditionally, matching Ingredient Stock's
     * layout exactly. `created` defaults to 0 when the response doesn't
     * carry it (Product Stock's response has no `created` field, since
     * Product Stock import never creates a product) — that's a true count,
     * not a fabricated one, so showing "0" here is a display choice, not a
     * change to what the backend reports.
     */
    async showStockImportSummary(summary) {
      const s = summary;
      const count = this.parseRowCount;
      if (!s || count(s.total_rows) === null || count(s.updated) === null ||
          count(s.unchanged) === null || count(s.failed_count) === null) {
        await this.showRequestError(this.$t("import_result"), this.$t("empty_import_response"));
        return;
      }
      const totalRows = count(s.total_rows);
      const created = count(s.created) ?? 0;
      const updated = count(s.updated);
      const unchanged = count(s.unchanged);
      // Product Stock no longer writes stock directly (ExcelController::
      // commitStockPreview()/respondWithProductStockPurchase()) — a
      // successful import now creates a PENDING stock purchase request
      // instead, approved/rejected later from the Stock page. `updated` is
      // still sent (aliased to the request count) so this dialog doesn't
      // break, but it must not be presented as "already updated".
      const purchaseCode = s.purchase_code || null;
      const isPendingPurchase = Boolean(purchaseCode);
      const failedRows = Array.isArray(s.failed_rows) ? s.failed_rows : [];
      const failedCount = Math.max(count(s.failed_count), failedRows.length);
      const hasFailures = failedCount > 0;
      const MAX_ROWS_SHOWN = 100;

      const stockSuccessTable = this.buildStockSuccessRowsTable(s, MAX_ROWS_SHOWN);
      const createdStat = this.buildCreatedStatTile(created);
      const resultIcon = this.stockImportResultIcon(hasFailures, totalRows, isPendingPurchase);
      const pendingPurchaseBanner = this.buildPendingPurchaseBanner(purchaseCode);
      const stockFailuresTable = hasFailures
        ? this.buildStockFailedRowsTable(failedRows, MAX_ROWS_SHOWN)
        : "";

      const summaryResult = await this.$swal.fire({
        icon: resultIcon,
        title: `${this.$t(this.select.title)} ${this.$t("import_result")}`,
        width: 720,
        confirmButtonText: this.confirmLabel(),
        // vue-sweetalert2 here is pinned to SweetAlert2 v9 (see
        // package.json) — `showDenyButton`/`isDenied` don't exist until
        // v10.9, so they were silently ignored and no second button ever
        // rendered. `showCancelButton` is the only two-button option this
        // version has; repurposed here for "go to Purchase Orders" instead
        // of an actual cancel action.
        showCancelButton: isPendingPurchase,
        cancelButtonText: this.$t("view_purchase_requests"),
        cancelButtonColor: "#438EF2",
        reverseButtons: true,
        html: `
          <div class="import-summary">
            ${pendingPurchaseBanner}
            <div class="import-summary-stats import-summary-stats--stock">
              <div class="import-summary-stat import-summary-stat--total">
                <div class="import-summary-stat-value">${totalRows}</div>
                <div class="import-summary-stat-label">${this.escapeHtml(
                  this.$t("total_rows")
                )}</div>
              </div>${createdStat}
              <div class="import-summary-stat import-summary-stat--updated">
                <div class="import-summary-stat-value">${updated}</div>
                <div class="import-summary-stat-label">${this.escapeHtml(
                  this.$t(isPendingPurchase ? "stock_purchase_requested" : "updated")
                )}</div>
              </div>
              <div class="import-summary-stat import-summary-stat--unchanged">
                <div class="import-summary-stat-value">${unchanged}</div>
                <div class="import-summary-stat-label">${this.escapeHtml(
                  this.$t("unchanged")
                )}</div>
              </div>
              <div class="import-summary-stat import-summary-stat--failed">
                <div class="import-summary-stat-value">${failedCount}</div>
                <div class="import-summary-stat-label">${this.escapeHtml(
                  this.$t("failed")
                )}</div>
              </div>
            </div>
            ${stockSuccessTable}
            ${stockFailuresTable}
          </div>
        `,
        confirmButtonColor: "#438EF2",
      });

      if (isPendingPurchase && summaryResult.dismiss === "cancel") {
        this.$router.push("/report-purchase").catch(() => {});
      }
    },
    async showImportSummary(summary) {
      const s = summary || {};
      const created = s.created || 0;
      const updated = s.updated || 0;
      const failedCount = s.failed_count || 0;
      const hasFailures = failedCount > 0;

      const MAX_ROWS_SHOWN = 100;
      const failedRows = s.failed_rows || [];
      const shown = failedRows.slice(0, MAX_ROWS_SHOWN);
      const remaining = failedRows.length - shown.length;

      const tableRows = shown
        .map(
          (f) => `
            <tr>
              <td class="import-summary-row-cell">${this.escapeHtml(
                f.row
              )}</td>
              <td class="import-summary-error-cell">${this.escapeHtml(
                this.$t(f.error)
              )}</td>
            </tr>`
        )
        .join("");

      const moreRowsNote =
        remaining > 0
          ? `<div class="import-summary-more">${this.escapeHtml(
              this.$t("and_more_rows", { count: remaining })
            )}</div>`
          : "";

      const failuresTable = hasFailures
        ? `
              <div class="import-summary-table-wrap">
                <table class="import-summary-table">
                  <thead>
                    <tr>
                      <th scope="col" class="import-summary-row-cell">${this.escapeHtml(
                        this.$t("row")
                      )}</th>
                      <th scope="col" class="import-summary-error-cell">${this.escapeHtml(
                        this.$t("error")
                      )}</th>
                    </tr>
                  </thead>
                  <tbody>${tableRows}</tbody>
                </table>
              </div>
              ${moreRowsNote}
            `
        : "";

      await this.$swal.fire({
        icon: hasFailures ? "warning" : "success",
        title: this.$t("import_data"),
        width: hasFailures ? 520 : undefined,
        confirmButtonText: this.confirmLabel(),
        html: `
          <div class="import-summary">
            <div class="import-summary-stats">
              <div class="import-summary-stat import-summary-stat--created">
                <div class="import-summary-stat-value">${created}</div>
                <div class="import-summary-stat-label">${this.escapeHtml(
                  this.$t("created")
                )}</div>
              </div>
              <div class="import-summary-stat import-summary-stat--updated">
                <div class="import-summary-stat-value">${updated}</div>
                <div class="import-summary-stat-label">${this.escapeHtml(
                  this.$t("updated")
                )}</div>
              </div>
              <div class="import-summary-stat import-summary-stat--failed">
                <div class="import-summary-stat-value">${failedCount}</div>
                <div class="import-summary-stat-label">${this.escapeHtml(
                  this.$t("failed")
                )}</div>
              </div>
            </div>
            ${failuresTable}
          </div>
        `,
        confirmButtonColor: "#438EF2",
      });
    },
  },
};
</script>

<style scoped>
/* ---------- Landing (hero · steps · sections · cards) ---------- */
.import-page {
  min-height: 100%;
  background: #f7f8fc;
}
.imp-hero,
.imp-steps,
.imp-section {
  max-width: 1320px;
  margin-right: auto;
  margin-left: auto;
}

.imp-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 10px 2px 27px;
  border-bottom: 1px solid #dedee8;
  color: #252039;
}
.imp-kicker {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #7865be;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}
.imp-kicker__icon {
  display: inline-flex;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  border: 1px solid #d9d1f7;
  border-radius: 10px;
  background: #eeeafd;
  color: #5b39c9;
}
.imp-hero h1 {
  margin: 12px 0 6px;
  color: #252039;
  font-size: clamp(30px, 4vw, 44px);
  font-weight: 800;
  letter-spacing: -0.04em;
}
.imp-hero p {
  max-width: 570px;
  margin: 0;
  color: #858a9b;
  font-size: 14px;
  line-height: 1.6;
}
.imp-hero__badge {
  display: flex;
  min-width: 118px;
  flex-direction: column;
  padding: 14px 16px;
  border: 1px solid #e2deef;
  border-radius: 14px;
  background: #fff;
}
.imp-hero__badge strong {
  color: #5b39c9;
  font-size: 28px;
}
.imp-hero__badge span {
  color: #858a9b;
  font-size: 11px;
}

/* 3-step "how it works" strip, joined by a dashed rail */
.imp-steps {
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 26px;
  margin-bottom: 0;
  padding: 0;
  list-style: none;
}
.imp-step {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #e8eaf2;
  border-radius: 14px;
  background: #fff;
}
.imp-step:not(:last-child)::after {
  position: absolute;
  top: 50%;
  right: -13px;
  width: 14px;
  border-top: 2px dashed #cfc8ee;
  content: "";
}
.imp-step__num {
  display: inline-flex;
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #5b39c9;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
}
.imp-step__icon {
  display: inline-flex;
  color: #7865be;
}
.imp-step__text {
  color: #3a3552;
  font-size: 13px;
  font-weight: 700;
}

.imp-section {
  margin-top: 34px;
}
.imp-section__heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}
.imp-section__heading h2 {
  margin: 0;
  color: #252039;
  font-size: 21px;
  font-weight: 800;
}
.imp-section__heading p {
  margin: 5px 0 0;
  color: #858a9b;
  font-size: 13px;
}
.imp-section__heading .imp-notice {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid #f1dcb9;
  border-radius: 999px;
  background: #fff7ea;
  color: #a4611b;
  font-size: 12px;
  font-weight: 600;
}
.imp-section__count {
  min-width: 34px;
  padding: 7px 11px;
  border-radius: 8px;
  background: #edeafb;
  color: #6451ba;
  font-size: 12px;
  font-weight: 800;
  text-align: center;
}

.imp-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.imp-card {
  --imp-accent: #5b39c9;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e8eaf2;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 8px 20px rgba(47, 39, 83, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}
.imp-card::before {
  position: absolute;
  top: 0;
  left: 0;
  width: 36px;
  height: 4px;
  border-radius: 0 0 4px 0;
  background: var(--imp-accent);
  content: "";
  transition: width 0.25s ease;
}
.imp-card:hover {
  border-color: color-mix(in srgb, var(--imp-accent) 45%, #fff);
  box-shadow: 0 14px 28px rgba(47, 39, 83, 0.09);
  transform: translateY(-3px);
}
.imp-card:hover::before {
  width: 100%;
}

/* header band: tinted, with a faint spreadsheet-cell grid */
.imp-card__head {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 84px;
  padding: 0 20px;
  border-bottom: 1px solid color-mix(in srgb, var(--imp-accent) 16%, #fff);
  background-color: color-mix(in srgb, var(--imp-accent) 8%, #fff);
  background-image:
    linear-gradient(color-mix(in srgb, var(--imp-accent) 10%, transparent) 1px, transparent 1px),
    linear-gradient(90deg, color-mix(in srgb, var(--imp-accent) 10%, transparent) 1px, transparent 1px);
  background-size: 22px 22px;
}
.imp-card__icon {
  display: inline-flex;
  width: 48px;
  height: 48px;
  align-items: center;
  justify-content: center;
  border: 1px solid color-mix(in srgb, var(--imp-accent) 26%, #fff);
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 6px 14px color-mix(in srgb, var(--imp-accent) 16%, transparent);
}
.imp-card__icon .v-icon {
  color: var(--imp-accent);
  font-size: 20px;
}
.imp-card__index {
  color: color-mix(in srgb, var(--imp-accent) 55%, #fff);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
}

.imp-card__body {
  flex: 1;
  padding: 18px 20px 6px;
}
.imp-card__body h3 {
  margin: 0;
  color: #292440;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.3;
}
.imp-card__body p {
  min-height: 34px;
  margin: 6px 0 0;
  color: #8a8e9c;
  font-size: 12px;
  line-height: 1.45;
}
.imp-card__format {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 10px;
  padding: 4px 8px;
  border: 1px solid #e7e4ef;
  border-radius: 6px;
  background: #fff;
  color: #7b7790;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
}
.imp-card__format .v-icon {
  color: var(--imp-accent);
}

.imp-card__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 14px 20px 18px;
  padding-top: 14px;
  border-top: 1px dashed #e3e0ee;
}
.imp-card__template {
  padding: 0 8px !important;
  color: var(--imp-accent) !important;
  font-weight: 700;
}
.imp-card__import {
  border-radius: 9px;
  font-weight: 700;
}

@media (max-width: 1263px) {
  .imp-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 959px) {
  .imp-steps {
    grid-template-columns: minmax(0, 1fr);
  }
  .imp-step:not(:last-child)::after {
    display: none;
  }
  .imp-hero {
    align-items: flex-start;
  }
}
@media (max-width: 599px) {
  .imp-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .imp-hero__badge {
    display: none;
  }
  .imp-section__heading {
    align-items: flex-start;
    flex-direction: column;
  }
}
@media (prefers-reduced-motion: reduce) {
  .imp-card,
  .imp-card::before {
    transition: none;
  }
  .imp-card:hover {
    transform: none;
  }
}

/* ---------- Dark mode ---------- */
.theme--dark.import-page,
.theme--dark .import-page {
  background: transparent !important;
}
.theme--dark .imp-hero {
  border-bottom-color: rgba(255, 255, 255, 0.08);
  color: #fff;
}
.theme--dark .imp-hero h1 {
  color: #fff;
}
.theme--dark .imp-hero p,
.theme--dark .imp-hero__badge span,
.theme--dark .imp-section__heading p,
.theme--dark .imp-card__body p {
  color: #94a3b8;
}
.theme--dark .imp-kicker {
  color: #a5b4fc;
}
.theme--dark .imp-kicker__icon {
  border-color: rgba(129, 140, 248, 0.35);
  background: rgba(129, 140, 248, 0.15);
  color: #c4b5fd;
}
.theme--dark .imp-hero__badge,
.theme--dark .imp-step,
.theme--dark .imp-card {
  border-color: rgba(255, 255, 255, 0.08);
  background: #1e1e2d;
  box-shadow: none;
}
.theme--dark .imp-hero__badge strong {
  color: #c4b5fd;
}
.theme--dark .imp-step__text,
.theme--dark .imp-section__heading h2,
.theme--dark .imp-card__body h3 {
  color: #fff;
}
.theme--dark .imp-step__icon {
  color: #a5b4fc;
}
.theme--dark .imp-step:not(:last-child)::after {
  border-top-color: rgba(165, 180, 252, 0.35);
}
.theme--dark .imp-section__count {
  background: rgba(129, 140, 248, 0.15);
  color: #c4b5fd;
}
.theme--dark .imp-section__heading .imp-notice {
  border-color: rgba(245, 158, 11, 0.35);
  background: rgba(245, 158, 11, 0.12);
  color: #fbbf24;
}
.theme--dark .imp-card:hover {
  border-color: color-mix(in srgb, var(--imp-accent) 55%, #1e1e2d);
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.35);
}
.theme--dark .imp-card__head {
  border-bottom-color: rgba(255, 255, 255, 0.08);
  background-color: color-mix(in srgb, var(--imp-accent) 16%, #1e1e2d);
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
}
.theme--dark .imp-card__icon {
  border-color: rgba(255, 255, 255, 0.12);
  background: #1e1e2d;
  box-shadow: none;
}
.theme--dark .imp-card__format {
  border-color: rgba(255, 255, 255, 0.1);
  background: transparent;
  color: #94a3b8;
}
.theme--dark .imp-card__actions {
  border-top-color: rgba(255, 255, 255, 0.1);
}

.section-label {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 10px;
}

.import-file-input ::v-deep .v-input__slot {
  background: #f1f5f9 !important;
  border-radius: 10px !important;
  min-height: 46px;
  align-items: center;
}

.import-file-input ::v-deep fieldset {
  border-color: transparent !important;
  transition: border-color 0.2s ease;
}

.import-file-input ::v-deep .v-input--is-focused fieldset {
  border-color: #438ef2 !important;
  border-width: 1.5px !important;
}

/* Vuetify's own rule (`.v-text-field--enclosed .v-input__prepend-inner`)
   centers the icon with a fixed margin-top, which only lines up with this
   field's own min-height. Flex-center it instead so it stays centered
   regardless of height. */
.import-file-input ::v-deep .v-input__prepend-inner {
  margin-top: 2px !important;
  align-self: center;
  display: flex;
  align-items: center;
}
</style>

<!-- unscoped: this markup is injected into the SweetAlert2 popup at runtime, outside this component's DOM -->
<style>
.import-summary-popup {
  border-radius: 16px;
  padding-bottom: 20px;
}
.import-summary {
  text-align: left;
  padding: 0 4px;
  box-sizing: border-box;
}
.import-summary-scroll {
  max-height: 55vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
}
.import-error-text {
  text-align: left;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.6;
  margin: 0;
  color: #545454;
}
.import-summary-stats {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}
.import-summary-stats--stock {
  flex-wrap: wrap;
}
.import-summary-stat {
  flex: 1;
  min-width: 110px;
  border-radius: 10px;
  padding: 12px 8px;
  text-align: center;
}
.import-summary-stat-value {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
}
.import-summary-stat-label {
  font-size: 12px;
  opacity: 0.75;
  margin-top: 2px;
}
.import-summary-stat--created {
  background: #e6f7ee;
  color: #1b7e50;
}
.import-summary-stat--updated {
  background: #e8f1fd;
  color: #296bca;
}
.import-summary-stat--total {
  background: #f1f3f5;
  color: #495057;
}
.import-summary-pending-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff4e5;
  color: #92660a;
  border: 1px solid #f5e4b3;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 12px;
  font-size: 13px;
  line-height: 1.5;
}
.import-summary-pending-icon {
  flex-shrink: 0;
}
.import-summary-stat--unchanged {
  background: #fff4e5;
  color: #a95c05;
}
.import-summary-stat--failed {
  background: #fdeceb;
  color: #ca3427;
}
.import-summary-chip {
  display: inline-block;
  border-radius: 999px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 600;
  margin-right: 6px;
}
.import-summary-subtext {
  font-size: 12px;
  opacity: 0.75;
  margin: 2px 0 6px;
}
.import-summary-warning {
  font-size: 12px;
  color: #a95c05;
  background: #fff4e5;
  border-radius: 6px;
  padding: 4px 8px;
  margin-bottom: 6px;
}
.import-summary-table-wrap {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 8px;
}
.import-summary-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.import-summary-table thead th {
  position: sticky;
  top: 0;
  background: #fafafa;
  text-align: left;
  padding: 8px 10px;
  border-bottom: 1px solid #eee;
  font-weight: 600;
  color: #666;
}
.import-summary-table tbody td {
  padding: 6px 10px;
  border-bottom: 1px solid #f2f2f2;
  vertical-align: top;
}
.import-summary-table tbody tr:last-child td {
  border-bottom: none;
}
.import-summary-row-cell {
  width: 60px;
  font-weight: 600;
  color: #444;
  white-space: nowrap;
}
.import-summary-identifier-cell {
  width: 140px;
  color: #555;
  overflow-wrap: anywhere;
}
.import-summary-error-cell {
  color: #d73d30;
}
.import-summary-qty-cell {
  width: 100px;
  text-align: center;
  color: #1d8756;
  font-weight: 600;
}
.import-summary-success-table th {
  color: #1d8756;
}
.import-summary-more {
  margin-top: 8px;
  font-size: 12px;
  color: #757575;
  text-align: center;
}
.import-summary-new-items {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.import-summary-new-items-block {
  border: 1px solid #eee;
  border-radius: 10px;
  padding: 14px;
}
.import-summary-new-items-block + .import-summary-new-items-block {
  margin-top: 16px;
}
.import-summary-new-items-title {
  font-size: 15px;
  font-weight: 700;
  color: #2c2c2c;
  margin-bottom: 10px;
}
.import-summary-new-items-block ul {
  margin: 0;
  padding-left: 18px;
  list-style: disc;
}
.import-summary-new-items-block li {
  font-size: 14px;
  line-height: 1.5;
  color: #444;
  overflow-wrap: anywhere;
  margin-bottom: 4px;
}
.import-summary-new-items-block li:last-child {
  margin-bottom: 0;
}

/* Compare (preview) table — before/after stock values */
.import-compare-qty-cell {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.import-compare-diff--up {
  color: #1c8655;
  font-weight: 600;
}
.import-compare-diff--down {
  color: #d73d30;
  font-weight: 600;
}
.import-compare-diff--neutral {
  color: #757575;
}
.import-compare-row--error {
  background: #fdf4f3;
}
.import-compare-status {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: normal;
  text-align: left;
  line-height: 1.4;
}
.import-compare-status--ok {
  background: #e6f7ee;
  color: #1b7e50;
}
.import-compare-status--unchanged {
  background: #fff4e5;
  color: #a95c05;
}
.import-compare-status--error {
  background: #fdeceb;
  color: #ca3427;
}
.import-compare-empty {
  text-align: center !important;
  color: #999;
  padding: 24px 10px !important;
  font-size: 13px;
}

/* Preview & Compare dialog — plain app-custom-dialog chrome (title bar +
   footer come from that component) with a receipt-style stat row/table. */
.import-compare-demo-banner,
.import-compare-error-banner {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12px;
  line-height: 1.5;
  padding: 8px 0;
}
.import-compare-demo-banner {
  background: #fff8e6;
  color: #92660a;
}
.import-compare-error-banner {
  background: #fdeceb;
  color: #b3261e;
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 10px;
}
.import-compare-demo-banner .v-icon,
.import-compare-error-banner .v-icon {
  margin-top: 2px;
}
.import-compare-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6c7581;
  margin-bottom: 10px;
}
.import-compare-meta-sep {
  opacity: 0.6;
}
.import-compare-stats {
  display: flex;
  gap: 8px;
  margin: 0 0 14px;
  padding: 0;
  list-style: none;
}
.import-compare-stat {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 10px;
  padding: 10px 6px;
}
.import-compare-stat-value {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
}
.import-compare-stat-label {
  font-size: 11px;
  opacity: 0.8;
  margin-top: 2px;
  white-space: nowrap;
}
.import-compare-stat--total {
  background: #f1f3f5;
  color: #495057;
}
.import-compare-stat--updated {
  background: #e8f1fd;
  color: #296bca;
}
.import-compare-stat--unchanged {
  background: #fff4e5;
  color: #a95c05;
}
.import-compare-stat--failed {
  background: #fdeceb;
  color: #ca3427;
}
.import-compare-activator {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.import-compare-table-wrap {
  flex: 1 1 auto;
  min-height: 260px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 10px;
}
.import-compare-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.import-compare-table thead th {
  position: sticky;
  top: 0;
  background: #eef4fd;
  color: #2a6dcd;
  text-align: center;
  padding: 8px 10px;
  border-bottom: 1px solid #e3ecfa;
  font-weight: 700;
  line-height: 1.4;
}
.import-compare-table tbody td {
  padding: 8px 10px;
  border-bottom: 1px solid #f2f2f2;
  text-align: center;
}
.import-compare-name-cell {
  text-align: left !important;
}
.import-compare-table tbody tr:last-child td {
  border-bottom: none;
}
.import-compare-row--stripe {
  background: #f7f9fc;
}
</style>
